// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { isDefined, isNull } from '@shared/utils';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { Heading } from '@shared/ui-kit/components';
import { Table, Tabs } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { TagManagement } from '@shared/advisory/aggregation/components/tags';
import { TagObjectType } from '@shared/data-access/interfaces/redux/tags-management-state-types';
import {
  DocumentEntityType,
  IlliquidAssetType,
} from '@shared/data-access/types/engine-types';
// eslint-disable-next-line @nx/enforce-module-boundaries -- entity drawer embeds document-management; cycle with documents page Loader import
import { EntityDocumentsTab } from '@shared/document-management/components/entity-documents-tab';
import { IlliquidAssetFinancialEventsSection } from './illiquid-asset-financial-events-section';
import { IlliquidAssetLinkedAccountsSection } from './illiquid-asset-linked-accounts-section';
import { IlliquidAssetValueEvolutionSection } from './illiquid-asset-value-evolution-section';
import { classNames } from '@shared/ui-kit/utils/classnames';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon';
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import type { ListItemProps } from '@mui/material';
import type { PropsWithChildren } from 'react';
import { canShowIlliquidValuationChart } from '../../utils/build-illiquid-valuation-chart-points';
import { useIlliquidAssetDetailDrawer } from './use-illiquid-asset-detail-drawer';
import styles from './illiquid-asset-detail-drawer.module.css';

const CATEGORY_I18N_SLUG_BY_ASSET_TYPE: Record<IlliquidAssetType, string> = {
  [IlliquidAssetType.RealEstate]: 'real-estate',
  [IlliquidAssetType.Company]: 'business',
  [IlliquidAssetType.PeVc]: 'private-equity',
  [IlliquidAssetType.Tangible]: 'tangible',
  [IlliquidAssetType.OtherIlliquid]: 'other',
};

const ICON_BY_ASSET_TYPE: Record<IlliquidAssetType, SvgIconName> = {
  [IlliquidAssetType.RealEstate]: 'Building',
  [IlliquidAssetType.Company]: 'Briefcase',
  [IlliquidAssetType.PeVc]: 'TrendingUp',
  [IlliquidAssetType.Tangible]: 'Palette',
  [IlliquidAssetType.OtherIlliquid]: 'Boxes',
};

export const IlliquidAssetDetailDrawer = defineComponent({
  displayName: 'IlliquidAssetDetailDrawer',
  useSetup: useIlliquidAssetDetailDrawer,
  Slots: {
    Title: ({ children }: PropsWithChildren) => (
      <Typography variant="subtitle2" className={styles['section-title']}>
        {children}
      </Typography>
    ),
    Section: ({
      divider,
      className,
      ...props
    }: ListItemProps & { divider?: boolean }) => (
      <>
        <ListItem
          className={classNames(styles.section, className)}
          disablePadding
          disableGutters
          {...props}
        >
          {props.children}
        </ListItem>
        {divider && <Divider />}
      </>
    ),
  },
  View: ({
    DrawerRenderer,
    onClose,
    t,
    asset,
    Slots,
    isAcquisitionAsset,
    acquisitionPerformanceRatio,
    tagManagement,
    onDelete,
    draftIbans,
    onLinkIban,
    onUnlinkIban,
    isDirty,
    isSaving,
    onSaveLinkedAccounts,
  }) => (
    <DrawerRenderer
      anchor="right"
      PaperProps={{
        classes: {
          root: styles['drawer-paper'],
        },
      }}
    >
      <Tabs.Root
        defaultValue="info"
        baseId="illiquid-asset-detail-tabs"
        variant="pill"
        className={styles['drawer-tabs']}
      >
        <List dense disablePadding className={styles['drawer-list']}>
          <Slots.Section
            className={classNames(
              styles['section-sticky-top'],
              styles['section-header'],
            )}
            divider
          >
            <header className={styles['asset-header']}>
              <figure className={styles['asset-icon-wrap']}>
                <AppIcon
                  icon={
                    ICON_BY_ASSET_TYPE[
                      asset?.category ?? IlliquidAssetType.OtherIlliquid
                    ]
                  }
                  color="disabled"
                  size="lg"
                />
              </figure>

              <div className={styles['asset-header-content']}>
                <Typography variant="body2" className={styles['asset-type-line']}>
                  <Translation
                    i18nKey={
                      `aggregator.illiquid-asset-category.${
                        CATEGORY_I18N_SLUG_BY_ASSET_TYPE[
                          asset?.category ?? IlliquidAssetType.OtherIlliquid
                        ]
                      }` as any
                    }
                    ns="aggregator"
                  />
                </Typography>

                <Typography variant="h5" className={styles['asset-name']}>
                  {asset?.name}
                </Typography>
              </div>
            </header>

            <div className={styles['header-close-wrap']}>
              <IconButton
                aria-label={t('common.close', { ns: 'common' })}
                size="small"
                className={classNames(
                  styles['close-button'],
                  styles['header-close'],
                )}
                onClick={onClose}
                data-testid="illiquid-asset-detail-drawer-close"
              >
                <AppIcon icon="Close" color="inherit" />
              </IconButton>
            </div>

            <section className={styles['summary-block']}>
              <section className={styles['kpi-grid']}>
                <figure className={styles['kpi-card']}>
                  <Heading
                    variant="h5"
                    className="w-full justify-between uppercase max-md:!text-base"
                    title={
                      <Translation
                        i18nKey="illiquid-asset-drawer.section.current-valuation"
                        ns="components"
                      />
                    }
                  />
                  <p className="money-display text-lg font-semibold text-graph-text md:text-2xl">
                    <Table.Data
                      value={asset?.local_currency_values?.current_valuation}
                      format="currency"
                      currency={asset?.local_currency_values?.currency}
                      safeValue="-"
                      emptyValue={null}
                    />
                  </p>
                </figure>

                <figure className={styles['kpi-card']}>
                  <Heading
                    variant="h5"
                    className="w-full justify-between uppercase max-md:!text-base"
                    title={
                      <Translation
                        i18nKey="illiquid-asset-drawer.section.acquisition"
                        ns="components"
                      />
                    }
                  />
                  <p className="money-display text-lg font-semibold text-graph-text md:text-2xl">
                    {isAcquisitionAsset ? (
                      <Table.Data
                        value={asset?.local_currency_values?.acquisition_value}
                        format="currency"
                        currency={asset?.local_currency_values?.currency}
                        safeValue="-"
                        emptyValue={null}
                      />
                    ) : (
                      '-'
                    )}
                  </p>
                  {acquisitionPerformanceRatio !== null && (
                    <div className={styles['kpi-performance']}>
                      <Table.Data
                        value={acquisitionPerformanceRatio}
                        format="percentage"
                        validate
                        formatOptions={{
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                          signDisplay: 'exceptZero',
                        }}
                        safeValue="-"
                      />
                    </div>
                  )}
                </figure>
              </section>

              <Tabs.List>
                <Tabs.Trigger value="info">
                  <Translation
                    i18nKey="illiquid-asset-drawer.tabs.info"
                    ns="components"
                  />
                </Tabs.Trigger>
                <Tabs.Trigger value="docs">
                  <span className="inline-flex items-center gap-2">
                    <Translation
                      i18nKey="illiquid-asset-drawer.tabs.docs"
                      ns="components"
                    />
                  </span>
                </Tabs.Trigger>
              </Tabs.List>
            </section>
          </Slots.Section>

          <Slots.Section>
            <Tabs.Content value="info" container={false} className="w-full">
              <div className="flex w-full flex-col gap-4">
                <section className="flex w-full flex-col gap-3">
                  <Slots.Title>
                    <Translation
                      i18nKey="illiquid-asset-drawer.section.basic-information"
                      ns="components"
                    />
                  </Slots.Title>

                  <dl className={styles['info-grid']}>
                    <dt className={styles['info-term']}>
                      <span className={styles['info-label']}>
                        <AppIcon icon="Calendar" size="base" />
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.acquisition-date"
                          ns="components"
                        />
                      </span>
                    </dt>
                    <dd className={styles['info-value']}>
                      <Table.Data
                        value={asset?.acquisition_date}
                        format="date"
                        safeValue="-"
                        emptyValue={null}
                      />
                    </dd>

                    <dt className={styles['info-term']}>
                      <span className={styles['info-label']}>
                        <AppIcon icon="Calendar" size="base" />
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.valuation-date"
                          ns="components"
                        />
                      </span>
                    </dt>
                    <dd className={styles['info-value']}>
                      <Table.Data
                        value={asset?.valuation_date}
                        format="date"
                        safeValue="-"
                        emptyValue={null}
                      />
                    </dd>

                    <dt className={styles['info-term']}>
                      <span className={styles['info-label']}>
                        <AppIcon icon="Percent" size="base" />
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.ownership-percent"
                          ns="components"
                        />
                      </span>
                    </dt>
                    <dd className={styles['info-value']}>
                      {isAcquisitionAsset ? (
                        <Table.Data
                          value={asset?.ownership_pct}
                          format="percentage"
                          safeValue="-"
                          emptyValue={null}
                        />
                      ) : (
                        '-'
                      )}
                    </dd>

                    <dt className={styles['info-term']}>
                      <span className={styles['info-label']}>
                        <AppIcon icon="DollarSign" size="base" />
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.invested-capital"
                          ns="components"
                        />
                      </span>
                    </dt>
                    <dd className={styles['info-value']}>
                      <Table.Data
                        value={
                          asset?.category === IlliquidAssetType.PeVc
                            ? asset?.local_currency_values?.total_commitment
                            : asset?.local_currency_values?.acquisition_value
                        }
                        format="currency"
                        currency={asset?.local_currency_values?.currency}
                        safeValue="-"
                        emptyValue={null}
                      />
                    </dd>

                    {!!asset?.description && (
                      <dd className={styles['description']}>
                        {asset?.description}
                      </dd>
                    )}
                  </dl>
                </section>

                {asset &&
                  isDefined(asset) &&
                  !isNull(asset.id) &&
                  canShowIlliquidValuationChart(
                    asset.events,
                    asset.local_currency_values?.currency ?? asset.currency,
                  ) && (
                    <>
                      <Divider />
                      <IlliquidAssetValueEvolutionSection
                        events={asset.events}
                        currency={
                          asset.local_currency_values?.currency ?? asset.currency
                        }
                      />
                    </>
                  )}

                {asset && isDefined(asset) && !isNull(asset.id) && (
                  <>
                    <Divider />
                    <IlliquidAssetFinancialEventsSection asset={asset} />
                  </>
                )}

                {isDefined(asset) && !isNull(asset.id) && (
                  <>
                    <Divider />
                    <IlliquidAssetLinkedAccountsSection
                      draftIbans={draftIbans}
                      onLink={onLinkIban}
                      onUnlink={onUnlinkIban}
                    />
                  </>
                )}

                {tagManagement && isDefined(asset) && !isNull(asset.id) && (
                  <>
                    <Divider />
                    <div className="flex w-full flex-col gap-2">
                      <TagManagement
                        objectType={TagObjectType.IlliquidAsset}
                        objectId={String(asset.id)}
                        allTags={tagManagement.tags}
                        initialAssignedTags={tagManagement.initialAssignedTags}
                        onChange={tagManagement.onChangeTagIds}
                        onCreateTag={tagManagement.onCreateTag}
                      />
                    </div>
                  </>
                )}
              </div>
            </Tabs.Content>

            <Tabs.Content value="docs" container={false} className="w-full">
              {isDefined(asset) && !isNull(asset.id) ? (
                <EntityDocumentsTab
                  entityType={DocumentEntityType.IlliquidAssets}
                  entityId={String(asset.id)}
                />
              ) : null}
            </Tabs.Content>
          </Slots.Section>

          <Slots.Section className={styles['section-spacer']} />

          <Slots.Section className={styles['section-sticky-bottom']}>
            <div className={styles['footer-actions']}>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                fullWidth
                disabled={!isDirty || isSaving}
                onClick={onSaveLinkedAccounts}
                startIcon={
                  isSaving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                <Translation i18nKey="common.save-changes" ns="common" />
              </Button>

              <Button
                type="button"
                variant="text"
                color="error"
                fullWidth
                onClick={onDelete}
                disabled={isSaving}
                startIcon={<AppIcon icon="Trashcan" color="inherit" size="lg" />}
              >
                <Translation
                  i18nKey="illiquid-asset-drawer.delete-asset"
                  ns="components"
                />
              </Button>
            </div>
          </Slots.Section>
        </List>
      </Tabs.Root>
    </DrawerRenderer>
  ),
});
